'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import { useEffect, useState } from 'react';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';

import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref } from '@eliadmin/core/src/utils/url-utils';
import { LuMenu, LuPlus, LuX } from 'react-icons/lu';
import { MdOutlineKeyboardArrowLeft, MdOutlineKeyboardArrowRight } from 'react-icons/md';

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories: CategoriesEntity[];
  setIsSidebarOpen: (open: boolean) => void;
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const menuLabel = locale === 'sk' ? 'Otvoriť navigáciu' : 'Open navigation';
  const closeLabel = locale === 'sk' ? 'Zavrieť' : 'Close';
  const { isOpen, setIsOpen } = useDisclosure();
  const [isOpenLevel2, setIsOpenLevel2] = useState(false);

  const handleCloseAll = () => {
    setIsOpenLevel2(false);
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen || isOpenLevel2) props.setIsSidebarOpen(true);
    else props.setIsSidebarOpen(false);
  }, [isOpen, isOpenLevel2]);

  return (
    <>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger
          aria-label={menuLabel}
          className={cn('text-black', 'ea-mobile-sidebar__trigger', props.className?.trigger)}
        >
          <LuMenu size={24} />
        </SheetTrigger>
        <SheetContent
          side={'left'}
          className={cn(
            'w-full max-w-[340px] border-none p-0 shadow-none sm:max-w-[340px]',
            'ea-mobile-sidebar__content',
            props.className?.content,
          )}
          showCloseIcon={false}
        >
          <div className="flex items-center justify-end p-4">
            <SheetClose aria-label={closeLabel}>
              <LuX size={20} className="text-muted-foreground" />
            </SheetClose>
          </div>
          <div className="flex w-full flex-col items-start justify-start gap-6 px-4 py-8">
            {props.menu?.map((item, index) => {
              const href = getSafeHref(item?.path ?? '#');

              if (index === 2) {
                return (
                  <Sheet
                    open={isOpenLevel2}
                    onOpenChange={open => {
                      setIsOpenLevel2(open);
                    }}
                    key={item.name}
                  >
                    <SheetTrigger className="flex items-center justify-start gap-2 text-lg tracking-tighter text-black">
                      {item.name} <LuPlus size={20} className="text-primary" />
                    </SheetTrigger>
                    <SheetContent
                      side={'left'}
                      className="w-full max-w-[340px] border-none p-0 shadow-none sm:max-w-[340px]"
                      showCloseIcon={false}
                    >
                      <div className="flex w-full items-center justify-end p-4">
                        <button type="button" aria-label={closeLabel} onClick={handleCloseAll}>
                          <LuX size={20} className="text-muted-foreground" />
                        </button>
                      </div>
                      <div className="flex w-full flex-col items-start justify-start gap-6 px-4 py-8">
                        <div
                          onClick={() => setIsOpenLevel2(false)}
                          className="flex items-center justify-start gap-2 text-lg font-medium tracking-tighter"
                        >
                          <MdOutlineKeyboardArrowLeft /> {t('text150')}
                        </div>
                        <div className="flex w-full flex-col items-start justify-start gap-4">
                          {props.categories.map(category => (
                            <Link prefetchOnHover
                              key={category.slug}
                              href={ROUTES.CATEGORY({ slug: category.slug, page: 1 })}
                              onClick={handleCloseAll}
                              className="flex w-full items-center justify-between gap-4 rounded-[16px] bg-[#F9FAFB] p-4"
                            >
                              <Image
                                src={category?.icon?.url ?? '/images/placeholder.png'}
                                alt={resolveImageAlt(category?.icon, category?.name)}
                                width={0}
                                height={0}
                                className="max-h-6 min-h-6 min-w-6 max-w-6 object-contain"
                              />
                              <p className="w-full text-base font-medium tracking-tight">
                                {category.name}
                              </p>
                              <MdOutlineKeyboardArrowRight size={24} className="text-primary" />
                            </Link>
                          ))}
                        </div>
                      </div>
                    </SheetContent>
                  </Sheet>
                );
              }

              return (
                <SheetClose
                  key={item.name}
                  asChild
                  className="text-lg font-medium tracking-tighter text-black"
                >
                  <Link prefetchOnHover prefetch={false} href={href}>
                    {item.name}
                  </Link>
                </SheetClose>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
